/**
 * MODULE: _shared/frontend-shell/src/layout/appShellDocumentTitle.tsx
 * PURPOSE: Map AppLayout routes → `document.title` and sync on navigation (CRM / Sales Hub excluded)
 * DEPENDENCIES: react, react-router-dom
 * PUBLIC_API: appShellDocumentTitle, AppShellDocumentTitle
 * SIDE_EFFECTS: Sets `document.title` on pathname change (component only)
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 72 / 120 lines
 * RECENT: 2026-05-21 [2.2.1118] — fix stale document titles on `/accounts`, `/settings`, and other non-CRM routes. [2.2.1127] docs compliance MODULE sync.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BRAZN_SUFFIX = ' · Brazn';

/** Routes that own `document.title` via dedicated shell sync components. */
function isDedicatedTitleRoute(pathname: string): boolean {
  const p = (pathname.replace(/\/$/, '') || pathname).trim();
  return p === '/crm' || p.startsWith('/crm/') || p === '/sales-hub' || p.startsWith('/sales-hub/');
}

/**
 * Returns a title for AppLayout-managed pages, or `null` when CRM / Sales Hub shells should own the tab.
 */
export function appShellDocumentTitle(pathname: string): string | null {
  const p = (pathname.replace(/\/$/, '') || pathname).trim() || '/';

  if (isDedicatedTitleRoute(p)) {
    return null;
  }

  if (p === '/accounts' || p.startsWith('/accounts/')) {
    return `Accounts${BRAZN_SUFFIX}`;
  }
  if (p === '/settings' || p.startsWith('/settings/')) {
    return `Settings${BRAZN_SUFFIX}`;
  }
  if (p === '/' || p === '/dashboard') {
    return `Dashboard${BRAZN_SUFFIX}`;
  }
  if (
    p === '/home' ||
    p === '/langflow-assistant' ||
    p === '/assistant' ||
    p.startsWith('/langflow-assistant/')
  ) {
    return `Assistant${BRAZN_SUFFIX}`;
  }
  if (p === '/calls' || p.startsWith('/calls/')) {
    return `Calls${BRAZN_SUFFIX}`;
  }
  if (p === '/calendar' || p.startsWith('/calendar/')) {
    return `Calendar${BRAZN_SUFFIX}`;
  }
  if (p === '/messaging' || p.startsWith('/messaging/')) {
    return `Messaging${BRAZN_SUFFIX}`;
  }
  if (p === '/browser' || p.startsWith('/browser/')) {
    return `AI Browser${BRAZN_SUFFIX}`;
  }
  if (p === '/activity' || p.startsWith('/activity/')) {
    return `Activity${BRAZN_SUFFIX}`;
  }

  return null;
}

export function AppShellDocumentTitle() {
  const { pathname } = useLocation();

  useEffect(() => {
    const next = appShellDocumentTitle(pathname);
    if (next) {
      document.title = next;
    }
  }, [pathname]);

  return null;
}
